export interface FolderReconnectResult {
    handle: FileSystemDirectoryHandle;
    folderName: string;
    name?: string;
}

export interface ExecuteFolderReconnectOptions {
    result: FolderReconnectResult;
    pendingProjectLoad: any;
    FileSystemService: any;
    setParentFolder: (folder: any) => void;
    setQuoteSubfolder: (subfolder: any) => void;
    setMissingFiles: (updater: (prev: any) => any) => void;
    setPhotos: (photos: any) => void;
    setDocuments: (docs: any) => void;
    setFolderFiles: (files: any) => void;
    setPendingProjectLoad: (load: any) => void;
    setReconnectModalOpen: (open: boolean) => void;
    completeProjectLoad: (load: any) => void;
}

/**
 * Handles folder reconnection logic when reconnecting a sync folder to a project.
 * Updates folder handles, hydrates photos, documents, and videos from the disk directory.
 */
export async function executeFolderReconnect({
    result,
    pendingProjectLoad,
    FileSystemService,
    setParentFolder,
    setQuoteSubfolder,
    setMissingFiles,
    setPhotos,
    setDocuments,
    setFolderFiles,
    setPendingProjectLoad,
    setReconnectModalOpen,
    completeProjectLoad,
}: ExecuteFolderReconnectOptions): Promise<void> {
    setReconnectModalOpen(false);
    setParentFolder({ name: result.name || result.folderName || '', handle: result.handle });

    // SyncFolderService mirror: if the user just reconnected the sync
    // root, update the shared service singleton too so Stage 1 / Stage
    // 5 video players can read media blobs using this newly-granted
    // handle.
    try {
        const { SyncFolderService } = await import('./sync-folder');
        if (typeof SyncFolderService?.setHandle === 'function') {
            await SyncFolderService.setHandle(result.handle);
        }
    } catch (err: any) {
        console.warn('[Reconnect] could not mirror handle to SyncFolderService:', err?.message);
    }

    const savedSubfolderName = pendingProjectLoad?.projectFolder?.subfolderName;
    let subfolderHandle: any = null;

    if (savedSubfolderName) {
        try {
            subfolderHandle = await FileSystemService.openSubfolder(result.handle, savedSubfolderName);
            if (subfolderHandle) {
                setQuoteSubfolder({ name: savedSubfolderName, handle: subfolderHandle });
            } else {
                setMissingFiles((prev: any) => ({ ...prev, subfolder: savedSubfolderName }));
            }
        } catch (err) {
            console.warn('Could not open subfolder:', savedSubfolderName, err);
            setMissingFiles((prev: any) => ({ ...prev, subfolder: savedSubfolderName }));
        }
    }

    if (pendingProjectLoad?.projectFolder?.files) {
        const files = pendingProjectLoad.projectFolder.files;
        const directoryHandle = subfolderHandle || result.handle;

        if (files.photos?.length > 0) {
            const { photos: loadedPhotos, missing: missingPhotos } = await FileSystemService.loadPhotosFromSubfolder(directoryHandle, files.photos);
            setPhotos(loadedPhotos);
            if (missingPhotos.length > 0) {
                setMissingFiles((prev: any) => ({ ...prev, photos: missingPhotos }));
            }
        }

        if (files.documents?.length > 0) {
            const { documents: loadedDocs, missing: missingDocs } = await FileSystemService.loadDocumentsFromSubfolder(directoryHandle, files.documents);
            const recordDocs = Array.isArray(pendingProjectLoad.documents) ? pendingProjectLoad.documents : null;
            let mergedDocs = loadedDocs;
            if (recordDocs) {
                const keyOf = (d: any) => String(d?.id || d?.name || '').toLowerCase();
                const diskByKey = new Map((loadedDocs || []).map((d: any) => [keyOf(d), d]));
                const seen = new Set();
                mergedDocs = recordDocs.map((rd: any) => {
                    const k = keyOf(rd); seen.add(k);
                    const disk = diskByKey.get(k);
                    return disk ? { ...rd, ...disk } : rd;
                });
                for (const dd of (loadedDocs || [])) {
                    if (!seen.has(keyOf(dd))) mergedDocs.push(dd);
                }
            }
            setDocuments(mergedDocs);
            pendingProjectLoad.documents = mergedDocs;
            if (missingDocs.length > 0) {
                setMissingFiles((prev: any) => ({ ...prev, documents: missingDocs }));
            }
        }

        setFolderFiles(files);
    }

    // Hydrate videos from the `videos/` subfolder
    try {
        const videoHandle = subfolderHandle || result.handle;
        const { videos: loadedVideos } = await FileSystemService.loadVideosFromSubfolder(videoHandle);
        if (loadedVideos.length > 0) {
            const dexieVideos = Array.isArray(pendingProjectLoad.videos) ? pendingProjectLoad.videos : [];
            const mergedVideos = loadedVideos.map((lv: any) => {
                const match = dexieVideos.find((dv: any) =>
                    (dv.syncFileName && dv.syncFileName === lv.syncFileName)
                    || (dv.name && dv.name === lv.name)
                );
                return match
                    ? { ...match, file: lv.file, url: lv.url, size: lv.size }
                    : lv;
            });
            pendingProjectLoad.videos = mergedVideos;
        }
    } catch (vidErr) {
        console.warn('Could not hydrate videos from folder:', vidErr);
    }

    completeProjectLoad(pendingProjectLoad);
    setPendingProjectLoad(null);
}
